import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { EncabezadoPagina } from '@xiltrion/ui/bloques/encabezado-pagina';
import { Alert, AlertDescription, AlertTitle } from '@xiltrion/ui/alert';
import { Button } from '@xiltrion/ui/button';
import { EstadoCargando, EstadoVacio } from '@xiltrion/ui/bloques/estados';
import { TarjetaIndicador } from '@xiltrion/ui/bloques/indicadores';
import { Card, CardContent, CardHeader, CardTitle } from '@xiltrion/ui/card';
import { Icono, type NombreIcono } from '@xiltrion/ui/icono';
import { Item, ItemContent, ItemDescription, ItemTitle } from '@xiltrion/ui/item';
import { fondoEtiqueta } from '@xiltrion/ui/paleta';
import { cn } from '@xiltrion/ui/utils';
import { apiGet } from '@/api/client';
import { useSession, useTerms } from '@/app/session';
import { Slot } from '@/app/Slot';
import { formatDate } from '../tasks/view-logic';

interface HomeTask { id: string; number: number; title: string; dueDate: string | null; priority: string | null; projectId: string; projectName: string; projectColor: number }
interface HomeData { today: string; overdue: HomeTask[]; today_: HomeTask[]; upcoming: HomeTask[]; focus: HomeTask[]; recentProjects: Array<{ id: string; name: string; color: number; icon: string }>; stats: { open: number; noDate: number; completedThisWeek: number; teamInbox: number } }

/** Inicio (MeisterTask Home): vencidas, de hoy, próximas, foco y proyectos recientes. */
export function Home() {
  const navigate = useNavigate();
  const { me } = useSession();
  const { lower } = useTerms();
  const { data, isLoading } = useQuery({ queryKey: ['home'], queryFn: () => apiGet<HomeData>('/home') });
  if (isLoading || !data) return <div className="p-6"><EstadoCargando variante="tarjeta" /></div>;
  const hour = new Date().getHours();
  const List = ({ title, icon, items, tone }: { title: string; icon: NombreIcono; items: HomeTask[]; tone?: string }) => (
    <Card>
      <CardHeader><CardTitle className={cn('flex items-center gap-2 text-base', tone)}><Icono nombre={icon} />{title}<span className="tnum ml-auto text-sm font-normal text-muted-foreground">{items.length}</span></CardTitle></CardHeader>
      <CardContent className="space-y-1">
        {items.length === 0 ? <p className="text-sm text-muted-foreground">Nada por aquí.</p> : items.slice(0, 8).map((t) => (
          <Item key={t.id} size="sm" asChild><button type="button" className="w-full text-left" onClick={() => navigate(`/tareas/${t.id}`)}>
            <span className={cn('size-2 shrink-0 rounded-full', fondoEtiqueta(t.projectColor))} />
            <ItemContent><ItemTitle className="line-clamp-1">{t.title}</ItemTitle><ItemDescription>{t.projectName}{t.dueDate ? ` · ${formatDate(t.dueDate)}` : ''}</ItemDescription></ItemContent>
          </button></Item>
        ))}
      </CardContent>
    </Card>
  );
  return (
    <div className="space-y-6 p-6">
      <EncabezadoPagina titulo={`${hour < 12 ? 'Buenos días' : hour < 19 ? 'Buenas tardes' : 'Buenas noches'}, ${me?.displayName.split(' ')[0] ?? ''}`} descripcion={new Date(`${data.today}T12:00:00`).toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })} />
      {data.stats.teamInbox > 0 && (
        <Alert variant="warning">
          <Icono nombre="mdi:account-group-outline" />
          <AlertTitle className="tnum">{data.stats.teamInbox === 1 ? `1 tarea llegó a tu ${lower('team')} sin repartir` : `${data.stats.teamInbox} tareas llegaron a tu ${lower('team')} sin repartir`}</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center gap-2">Repártelas o tómalas.<Button size="sm" variant="outline" onClick={() => navigate('/mis-tareas?de=equipo')}>Ver</Button></AlertDescription>
        </Alert>
      )}
      <div className="grid gap-4 sm:grid-cols-3">
        <TarjetaIndicador titulo="Abiertas asignadas a ti" valor={data.stats.open} icono="mdi:format-list-checks" detalle={data.stats.noDate ? `${data.stats.noDate} sin fecha` : undefined} />
        <TarjetaIndicador titulo="Vencidas" valor={data.overdue.length} icono="mdi:clock-outline" semaforo={data.overdue.length ? 'RED' : 'GREEN'} />
        <TarjetaIndicador titulo="Completadas esta semana" valor={data.stats.completedThisWeek} icono="mdi:check-circle-outline" />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <List title="Vencidas" icon="mdi:alert-circle-outline" items={data.overdue} tone="text-destructive" />
        <List title="Para hoy" icon="mdi:calendar-check-outline" items={data.today_} />
        <List title="Próximos 7 días" icon="mdi:calendar-range" items={data.upcoming} />
        <List title="Foco" icon="mdi:star-outline" items={data.focus} />
      </div>
      <Slot name="home.widgets" />
      <Card>
        <CardHeader><CardTitle className="text-base">Proyectos recientes</CardTitle></CardHeader>
        <CardContent className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {data.recentProjects.length === 0 ? <EstadoVacio titulo="Aún no has trabajado en proyectos" icono={null} /> : data.recentProjects.map((p) => (
            <button key={p.id} type="button" onClick={() => navigate(`/proyectos/${p.id}`)} className="flex items-center gap-2 rounded-md border p-3 text-left text-sm hover:bg-muted/60">
              <span className={cn('flex size-7 items-center justify-center rounded text-white', fondoEtiqueta(p.color))}><Icono nombre={p.icon as NombreIcono} className="size-4" /></span>
              <span className="truncate font-medium">{p.name}</span>
            </button>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
